// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";

export const getIsPlaceOrderDisabled = (state: CustomAppState, cart?: Cart) => {
    const isPlacingOrder = state.pages.onePageCheckout.isPlacingOrder;

    const isCartEmpty = !cart || (cart.cartLines && cart.cartLines.length <= 0) || !cart?.cartLines;
    const isEditingAddresses = state.xCheckout.isEditingAddress;

    const pendingPaymentData = !cart?.paymentMethod && !cart?.requiresApproval;

    const carriersWithAdditionalFieldsIds = cart?.properties ? cart?.properties?.carriersWithAdditionalFieldsIds : "";
    const carriersWithAdditionalFieldsIdsList =
        (carriersWithAdditionalFieldsIds && carriersWithAdditionalFieldsIds.split(",")) || "";

    const shouldShowCustomShippingCarrier =
        cart?.carrier &&
        cart.carrier.id &&
        carriersWithAdditionalFieldsIdsList &&
        carriersWithAdditionalFieldsIdsList?.length &&
        carriersWithAdditionalFieldsIdsList.includes(cart.carrier.id);

    const isShippingCarrierFieldsEmpty =
        shouldShowCustomShippingCarrier && (!state.xCheckout.nameOfCarrier || !state.xCheckout.accountNumber);

    return (
        isPlacingOrder ||
        isCartEmpty ||
        isEditingAddresses ||
        cart?.paymentOptions?.paymentMethods?.length === 0 ||
        !cart.canCheckOut ||
        isShippingCarrierFieldsEmpty ||
        pendingPaymentData
    );
};

export const getIsSaveAddressDisabled = (state: CustomAppState) => {
    const shippingAddressFormState = state.pages.onePageCheckout.shippingAddressFormState;

    return (
        !shippingAddressFormState?.address.address1 ||
        !shippingAddressFormState?.address.companyName ||
        !shippingAddressFormState?.address.country?.id ||
        !shippingAddressFormState?.address.state?.id ||
        !shippingAddressFormState?.address.postalCode ||
        !shippingAddressFormState?.address.city ||
        !shippingAddressFormState?.address.phone ||
        !shippingAddressFormState?.address.email
    );
};

export const getCheckoutUrl = (state: ApplicationState, type: "shipping" | "reviewAndSubmit" = "shipping") => {
    if (
        getCurrentPage(state).type === "OnePageCheckoutPage" ||
        getSettingsCollection(state).cartSettings.onePageCheckout
    ) {
        return getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    }

    return type === "shipping"
        ? getPageLinkByPageType(state, "CheckoutShippingPage")?.url
        : getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
};

export const mapShippingCustomCarrier = (state: CustomAppState) => {
    return {
        carrierName: state.xCheckout.nameOfCarrier,
        accountNumber: state.xCheckout.accountNumber,
        shippingNotes: state.xCheckout.shippingNotes,
        shippingType: state.xCheckout.nameOfCarrier ? "Ship Complete" : "",
    };
};

export const getCheckoutCartId = (state: ApplicationState) => {
    if (
        getCurrentPage(state).type === "OnePageCheckoutPage" ||
        getSettingsCollection(state).cartSettings.onePageCheckout
    ) {
        return state.pages.onePageCheckout.cartId;
    }

    return state.pages.checkoutShipping.cartId || state.pages.checkoutReviewAndSubmit.cartId;
};
